/**
 * Upload validation utilities
 * Provides validation functions for file uploads
 */

import { MAX_NAME_LENGTH } from './upload-constants.js';

/**
 * Validates the relative path of a file for length constraints
 * Ensures folder and file names don't exceed database limits
 * 
 * @param {string} webkitRelativePath - The relative path of the file
 * @returns {{ isValid: boolean, error: string|null, errorParams: object }} Validation result
 */
export function validateRelativePath(webkitRelativePath) {
  if (!webkitRelativePath || typeof webkitRelativePath !== 'string' || webkitRelativePath.length === 0) {
    return { isValid: true, error: null, errorParams: {} };
  }

  const pathSegments = webkitRelativePath.split('/');
  const folderSegments = pathSegments.length > 1 ? pathSegments.slice(0, pathSegments.length - 1) : [];
  const fileSegment = pathSegments.length > 1 ? pathSegments[pathSegments.length - 1] : pathSegments[0];

  // Validate folder segments
  for (const segment of folderSegments) {
    if (segment.length > MAX_NAME_LENGTH) {
      return {
        isValid: false,
        error: 'menus.uploadFiles.folderSegmentTooLong',
        errorParams: { segment, maxLength: MAX_NAME_LENGTH },
      };
    }
  }

  // Validate file segment
  if (fileSegment.length > MAX_NAME_LENGTH) {
    return {
      isValid: false,
      error: 'menus.uploadFiles.fileNameTooLong',
      errorParams: { name: fileSegment, maxLength: MAX_NAME_LENGTH },
    };
  }

  return { isValid: true, error: null, errorParams: {} };
}

/**
 * Checks if a file extension is allowed for the given dialog type
 * 
 * @param {string} filename - Name of the file
 * @param {string[]} allowedExtensions - Array of allowed extensions (without dot)
 * @returns {boolean} True if extension is allowed
 */
export function isExtensionAllowed(filename, allowedExtensions) {
  if (!allowedExtensions || allowedExtensions.length === 0) {
    return true;
  }

  const match = /\.([^./\\]+)$/i.exec(filename);
  const extension = match ? match[1].toLowerCase() : '';

  return allowedExtensions.includes(extension);
}

/**
 * Filters a FileList by allowed extensions
 * 
 * @param {FileList|File[]} fileList - Files to filter
 * @param {string[]|null} allowedExtensions - Array of allowed extensions (without dot), null for no filtering
 * @returns {File[]} Filtered array of files
 */
export function filterFilesByExtension(fileList, allowedExtensions) {
  if (!allowedExtensions) {
    return Array.from(fileList);
  }

  const filesArray = Array.from(fileList);

  return filesArray.filter((file) => isExtensionAllowed(file.name, allowedExtensions));
}

/**
 * Validates legal category selection for a file
 * 
 * @param {object} file - File object with metadata
 * @param {object} legalCategories - Map of file UUIDs to legal category IDs
 * @returns {{ isValid: boolean, error: string|null }} Validation result
 */
export function validateLegalCategory(file, legalCategories) {
  const categoryId = legalCategories[file.meta.fileuuid];
  
  if (!categoryId) {
    return {
      isValid: false,
      error: 'menus.uploadFiles.pleaseSelectLegalCategory',
    };
  }

  return { isValid: true, error: null };
}

/**
 * Validates if all required legal information is provided for files
 * 
 * @param {Array} files - Array of files to validate
 * @param {boolean} showLegalFields - Whether legal fields should be shown
 * @param {object} legalCategories - Map of file UUIDs to legal category IDs
 * @returns {{ isValid: boolean, errors: Array }} Validation result with any errors
 */
export function validateLegalInformation(files, showLegalFields, legalCategories) {
  if (!showLegalFields) {
    return { isValid: true, errors: [] };
  }

  const errors = [];

  for (const file of files) {
    const validation = validateLegalCategory(file, legalCategories);
    if (!validation.isValid) {
      errors.push({
        fileId: file.meta.fileuuid,
        fileName: file.meta.filename,
        error: validation.error,
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Checks for duplicate files in the upload list
 * A duplicate is defined as a file with the same name, size, path, and node
 * 
 * @param {File} newFile - File to check
 * @param {Array} existingFiles - Array of already added files
 * @param {string} nodeId - Target node ID
 * @returns {boolean} True if file is a duplicate
 */
export function isDuplicateFile(newFile, existingFiles, nodeId) {
  return existingFiles.some((existingFile) => {
    return existingFile.meta.filename === newFile.name
      && existingFile.meta.filesize === newFile.size
      && existingFile.meta.webkitRelativePath === (newFile.webkitRelativePath || '')
      && existingFile.meta.nodeid === nodeId;
  });
}
